import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Copy, Crown, Shield, UserMinus, Check, X as XIcon, Upload, Loader2, Trash2 } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import {
  getGroupBySlug, getMyGroupMembership, listGroupMembers, listJoinRequests,
  approveJoinRequest, setMemberRole, kickMember, createInvite,
  updateGroup, setGroupRules, listGroupRules, deleteGroup,
} from "@/lib/groups.functions";

export const Route = createFileRoute("/comunidades/$slug/admin")({
  head: () => ({ meta: [{ title: "Admin — Viny Club" }] }),
  component: AdminPage,
});

function AdminPage() {
  const { slug } = Route.useParams();
  const { isAuthenticated, loading } = useCurrentUser();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const getGroup = useServerFn(getGroupBySlug);
  const getMem = useServerFn(getMyGroupMembership);
  const listMem = useServerFn(listGroupMembers);
  const listReq = useServerFn(listJoinRequests);
  const listRulesFn = useServerFn(listGroupRules);
  const approveFn = useServerFn(approveJoinRequest);
  const roleFn = useServerFn(setMemberRole);
  const kickFn = useServerFn(kickMember);
  const inviteFn = useServerFn(createInvite);
  const updateFn = useServerFn(updateGroup);
  const setRulesFn = useServerFn(setGroupRules);
  const deleteFn = useServerFn(deleteGroup);

  const groupQ = useQuery({ queryKey: ["group", slug], queryFn: () => getGroup({ data: { slug } }) });
  const groupId = groupQ.data?.id;
  const memQ = useQuery({
    queryKey: ["group-membership", groupId],
    queryFn: () => getMem({ data: { groupId: groupId! } }),
    enabled: !!groupId && isAuthenticated,
  });
  const membersQ = useQuery({
    queryKey: ["group-members", groupId],
    queryFn: () => listMem({ data: { groupId: groupId! } }),
    enabled: !!groupId,
  });
  const reqsQ = useQuery({
    queryKey: ["group-requests", groupId],
    queryFn: () => listReq({ data: { groupId: groupId! } }),
    enabled: !!groupId && memQ.data?.role === "admin",
  });
  const rulesQ = useQuery({
    queryKey: ["group-rules", groupId],
    queryFn: () => listRulesFn({ data: { groupId: groupId! } }),
    enabled: !!groupId,
  });

  const [rulesText, setRulesText] = useState("");
  useEffect(() => {
    if (rulesQ.data) setRulesText(rulesQ.data.map((r) => r.text).join("\n"));
  }, [rulesQ.data]);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [bannerUrl, setBannerUrl] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  useEffect(() => {
    if (groupQ.data) {
      setName(groupQ.data.name);
      setDescription(groupQ.data.description ?? "");
      setAvatarUrl(groupQ.data.avatar_url ?? "");
      setBannerUrl(groupQ.data.banner_url ?? "");
    }
  }, [groupQ.data]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["group-members", groupId] });
    qc.invalidateQueries({ queryKey: ["group-requests", groupId] });
  };

  const inviteMut = useMutation({
    mutationFn: () => inviteFn({ data: { groupId: groupId!, expiresInDays: 7 } }),
    onSuccess: (r) => {
      const url = `${window.location.origin}/comunidades/convite/${r.token}`;
      navigator.clipboard?.writeText(url);
      toast.success("Link copiado!");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (loading || groupQ.isLoading) return <AppShell><div className="p-8 text-center text-sm text-muted-foreground">Carregando...</div></AppShell>;
  if (!groupQ.data) return <AppShell><div className="p-8 text-center text-sm text-muted-foreground">Grupo não encontrado</div></AppShell>;
  if (!isAuthenticated) { navigate({ to: "/auth" }); return null; }
  if (memQ.data?.role !== "admin") {
    return <AppShell><div className="p-8 text-center text-sm text-muted-foreground">Apenas administradores.</div></AppShell>;
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl space-y-8 px-4 pb-16 pt-4">
        <Link to="/comunidades/$slug" params={{ slug }} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Voltar
        </Link>
        <div>
          <h1 className="font-display text-2xl font-semibold">Administração</h1>
          <p className="text-sm text-muted-foreground">{groupQ.data.name}</p>
        </div>

        {/* Solicitações pendentes */}
        {(reqsQ.data?.length ?? 0) > 0 && (
          <section className="rounded-2xl border border-[color:var(--hairline)] bg-surface/40 p-5">
            <h2 className="mb-3 text-sm font-semibold">Solicitações de entrada</h2>
            <div className="space-y-2">
              {reqsQ.data!.map((r: any) => (
                <div key={r.id} className="flex items-center gap-3">
                  <img loading="lazy" decoding="async" src={r.profile?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${r.profile?.username ?? "u"}`} alt="" className="h-9 w-9 rounded-full" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{r.profile?.display_name || r.profile?.username}</div>
                  </div>
                  <button
                    onClick={async () => {
                      await approveFn({ data: { requestId: r.id, approve: true } });
                      invalidate();
                      toast.success("Aprovado");
                    }}
                    className="grid h-8 w-8 place-items-center rounded-full bg-primary text-primary-foreground"
                  ><Check className="h-4 w-4" /></button>
                  <button
                    onClick={async () => {
                      await approveFn({ data: { requestId: r.id, approve: false } });
                      invalidate();
                    }}
                    className="grid h-8 w-8 place-items-center rounded-full border border-[color:var(--hairline)] text-muted-foreground"
                  ><XIcon className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Convites */}
        <section className="rounded-2xl border border-[color:var(--hairline)] bg-surface/40 p-5">
          <h2 className="mb-2 text-sm font-semibold">Convidar pessoas</h2>
          <p className="mb-3 text-xs text-muted-foreground">Crie um link de convite (válido por 7 dias).</p>
          <button
            onClick={() => inviteMut.mutate()}
            disabled={inviteMut.isPending}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
          >
            <Copy className="h-4 w-4" /> Gerar link
          </button>
        </section>

        {/* Editar grupo */}
        <section className="space-y-4 rounded-2xl border border-[color:var(--hairline)] bg-surface/40 p-5">
          <h2 className="text-sm font-semibold">Visual e descrição</h2>

          {/* Capa/Banner */}
          <ImageUploader
            label="Capa do grupo"
            hint="Recomendado 1600×400 · JPG ou PNG"
            aspect="banner"
            value={bannerUrl}
            onChange={setBannerUrl}
          />

          {/* Avatar */}
          <ImageUploader
            label="Ícone do grupo"
            hint="Recomendado 400×400 · JPG ou PNG"
            aspect="avatar"
            value={avatarUrl}
            onChange={setAvatarUrl}
          />

          <input value={name} onChange={(e) => setName(e.target.value)} className="h-10 w-full rounded-xl border border-[color:var(--hairline)] bg-background px-3 text-sm" placeholder="Nome" />
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="w-full rounded-xl border border-[color:var(--hairline)] bg-background px-3 py-2 text-sm" placeholder="Descrição" />
          <button
            onClick={async () => {
              await updateFn({ data: { groupId: groupId!, name, description, avatar_url: avatarUrl, banner_url: bannerUrl } });
              qc.invalidateQueries({ queryKey: ["group", slug] });
              toast.success("Atualizado");
            }}
            className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
          >
            Salvar
          </button>
        </section>

        {/* Regras */}
        <section className="rounded-2xl border border-[color:var(--hairline)] bg-surface/40 p-5">
          <h2 className="mb-2 text-sm font-semibold">Regras do grupo</h2>
          <p className="mb-2 text-xs text-muted-foreground">Uma regra por linha.</p>
          <textarea
            value={rulesText}
            onChange={(e) => setRulesText(e.target.value)}
            rows={6}
            className="w-full rounded-xl border border-[color:var(--hairline)] bg-background px-3 py-2 text-sm"
          />
          <button
            onClick={async () => {
              await setRulesFn({ data: { groupId: groupId!, rules: rulesText.split("\n") } });
              qc.invalidateQueries({ queryKey: ["group-rules", groupId] });
              toast.success("Regras salvas");
            }}
            className="mt-3 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
          >Salvar regras</button>
        </section>

        {/* Membros */}
        <section className="rounded-2xl border border-[color:var(--hairline)] bg-surface/40 p-5">
          <h2 className="mb-3 text-sm font-semibold">Membros ({membersQ.data?.length ?? 0})</h2>
          <div className="space-y-2">
            {(membersQ.data ?? []).map((m) => (
              <div key={m.user_id} className="flex items-center gap-3">
                <img loading="lazy" decoding="async" src={m.profile?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${m.profile?.username ?? "u"}`} alt="" className="h-9 w-9 rounded-full" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{m.profile?.display_name || m.profile?.username}</div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{m.role}</div>
                </div>
                {m.role !== "admin" && (
                  <>
                    <button
                      onClick={async () => {
                        await roleFn({ data: { groupId: groupId!, userId: m.user_id, role: m.role === "moderator" ? "member" : "moderator" } });
                        invalidate();
                      }}
                      className="grid h-8 w-8 place-items-center rounded-full border border-[color:var(--hairline)] text-muted-foreground hover:text-primary"
                      title={m.role === "moderator" ? "Rebaixar" : "Tornar moderador"}
                    ><Shield className="h-4 w-4" /></button>
                    <button
                      onClick={async () => {
                        if (!confirm("Remover este membro?")) return;
                        await kickFn({ data: { groupId: groupId!, userId: m.user_id } });
                        invalidate();
                      }}
                      className="grid h-8 w-8 place-items-center rounded-full border border-[color:var(--hairline)] text-muted-foreground hover:text-destructive"
                    ><UserMinus className="h-4 w-4" /></button>
                  </>
                )}
                {m.role === "admin" && <Crown className="h-4 w-4 text-amber-400" />}
              </div>
            ))}
          </div>
        </section>

        {/* Zona de perigo */}
        <section className="rounded-2xl border border-destructive/40 bg-destructive/5 p-5">
          <h2 className="mb-1 text-sm font-semibold text-destructive">Zona de perigo</h2>
          <p className="mb-3 text-xs text-muted-foreground">
            Excluir o grupo remove todos os posts, mensagens e membros. Esta ação é irreversível.
          </p>
          <button
            onClick={() => setShowDeleteDialog(true)}
            className="inline-flex items-center gap-2 rounded-full bg-destructive px-4 py-2 text-sm font-semibold text-destructive-foreground hover:opacity-90"
          >
            <Trash2 className="h-4 w-4" /> Excluir grupo
          </button>
        </section>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir grupo</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o grupo "{groupQ.data?.name}"? Todos os posts, mensagens e membros serão removidos permanentemente. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeleting}
              onClick={async () => {
                setIsDeleting(true);
                try {
                  await deleteFn({ data: { groupId: groupId! } });
                  toast.success("Grupo excluído com sucesso");
                  qc.invalidateQueries({ queryKey: ["my-groups"] });
                  navigate({ to: "/comunidades" });
                } catch (e: any) {
                  toast.error(e?.message ?? "Erro ao excluir o grupo");
                  setIsDeleting(false);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Excluindo…
                </span>
              ) : (
                "Excluir"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}

function ImageUploader({
  label,
  hint,
  aspect,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  aspect: "avatar" | "banner";
  value: string;
  onChange: (url: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  async function handleFile(file: File) {
    if (!file.type.startsWith("image/")) {
      toast.error("Selecione uma imagem");
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Máximo 6MB");
      return;
    }
    setUploading(true);
    try {
      const { data: sess } = await supabase.auth.getUser();
      const uid = sess.user?.id ?? "anon";
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const path = `${uid}/groups/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const bucket = "community-media";
      const { error: upErr } = await supabase.storage
        .from(bucket)
        .upload(path, file, { upsert: false, contentType: file.type });
      if (upErr) throw upErr;
      const { data: signed, error: sErr } = await supabase.storage
        .from(bucket)
        .createSignedUrl(path, 60 * 60 * 24 * 365 * 5);
      if (sErr || !signed) throw sErr ?? new Error("Falha ao gerar URL");
      onChange(signed.signedUrl);
      toast.success("Imagem enviada");
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao enviar");
    } finally {
      setUploading(false);
    }
  }

  const boxClass =
    aspect === "banner"
      ? "aspect-[4/1] w-full"
      : "h-24 w-24";

  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between">
        <label className="text-xs font-medium text-foreground">{label}</label>
        {hint && <span className="text-[10px] text-muted-foreground">{hint}</span>}
      </div>
      <div className="flex items-center gap-3">
        <div className={`${boxClass} shrink-0 overflow-hidden rounded-xl border border-[color:var(--hairline)] bg-background`}>
          {value ? (
            <img loading="lazy" decoding="async" src={value} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="grid h-full w-full place-items-center text-[10px] uppercase tracking-wider text-muted-foreground">
              vazio
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 rounded-full border border-[color:var(--hairline)] bg-background px-3 py-1.5 text-xs font-medium hover:bg-surface disabled:opacity-60"
          >
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {value ? "Trocar imagem" : "Enviar imagem"}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="text-[11px] text-muted-foreground hover:text-destructive"
            >
              Remover
            </button>
          )}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
          e.target.value = "";
        }}
      />
    </div>
  );
}
